import { makeObservable, observable, flow } from "mobx";
import { toast } from "sonner";

import { api } from "@/lib/services";

import SubscriberSummarySchema from "@/areas/subscriptions/schemas/SubscriberSummarySchema";
import UpdateSubscriberSchema from "@/areas/subscriptions/schemas/UpdateSubscriberSchema";
import {
  beginSubscriberToast,
  completeSubscriberToast,
  failSubscriberToast,
} from "@/areas/subscriptions/components/subscribers/subscriberToast";

export default class SubscriberSummaryStore {
  loading = false;
  processing = false;
  summary = null;

  constructor(parentStore, apiPrefix) {
    makeObservable(this, {
      loading: observable,
      processing: observable,
      summary: observable,
      load: flow,
      assign: flow,
    });

    this.parentStore = parentStore;
    this.apiPrefix = apiPrefix;
  }

  *assign({ user, subscriber }) {
    let toastId;
    try {
      let subscriptions = this.summary?.subscriptions ?? [];
      if (subscriber.type == "coordinator" || subscriber.type == "accountable") {
        subscriptions = subscriptions.filter((x) => x.type !== subscriber.type);
      }
      subscriptions.push({ roleId: subscriber.id, type: subscriber.type, isNotificationEnabled: true });
      const data = {
        user: {
          id: user.userId,
          name: user.name,
          jobTitle: user.jobTitle,
        },
        subscriptions: subscriptions,
      };
      this.processing = true;
      const parseResult = UpdateSubscriberSchema.safeParse(data);
      if (!parseResult.success) {
        console.error(parseResult);
        return parseResult;
      }

      toastId = beginSubscriberToast({
        user: data.user,
        roles: [{ name: subscriber.name }],
        message: "Adding as",
      });

      const response = yield api.post(`${this.apiPrefix}/subscribers`, data);

      completeSubscriberToast({
        toastId,
        user: data.user,
        roles: [{ name: subscriber.name }],
        message: "Added as",
      });

      yield this.load({});
      return { success: true, data: response.data };
    } catch (e) {
      failSubscriberToast({
        toastId,
        message: `Error while adding to ${subscriber.name} role`,
        description: e.message,
      });
      return { success: false, error: e };
    } finally {
      this.processing = false;
    }
  }

  *load(query, options) {
    const parseResult = SubscriberSummarySchema.safeParse(query);
    if (!parseResult.success) {
      return parseResult;
    }
    try {
      this.summary = null;
      this.loading = true;
      const response = yield api.get(`${this.apiPrefix}/subscribers/summary`, {
        params: query,
      });
      this.summary = response.data;

      return { success: true, data: response.data };
    } catch (e) {
      toast.error("Error while loading subscribers", {
        description: e.message || "Unexpected error, please try again later.",
      });
      return { success: false, error: e };
    } finally {
      this.loading = false;
    }
  }
}
